import React, { useState, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import TickerRow from '../../../TickerRow/TickerRow';
import { useAuth } from 'react-oidc-context';
import styles from './style/NewPortfolioModal.module.css'; // 동일한 스타일을 사용합니다
import { useTranslation } from 'react-i18next'; // 💡 useTranslation 훅 임포트

const MessageModal = ({ message, onClose }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.messageModalBackdrop}>
      <div className={styles.messageModalContent}>
        <p>{message}</p>
        <button onClick={onClose} className={styles.messageModalCloseButton}>
          {t('rebalanceModal.alert.confirm')}
        </button>
      </div>
    </div>
  );
};

export default function RebalanceModal({ portfolioId, onClose, onRebalanced, theme }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const today = new Date().toISOString().slice(0, 10);
  
  // 💡 [추가] 데이터 로딩 및 제출 상태 관리
  const [portfolio, setPortfolio] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 💡 [추가] 포트폴리오 이름 상태
  const [portfolioName, setPortfolioName] = useState(''); 

  const [snapshots, setSnapshots] = useState([
    { rebalanceAt: today, holdings: [{ ticker: '', weight: 0 }] }
  ]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState('');

  const isActionDisabled = auth.isLoading || !auth.isAuthenticated;

  // 💡 [수정] 두 가지 상이한 Holdings 구조를 표준 형식으로 변환하는 헬퍼 함수
  const normalizeHoldings = (raw) => {
    if (!raw) return [];
    
    // 🔍 **디버그 시작** (유지)
    console.log('--- normalizeHoldings: 원시 데이터 (Raw Data) ---');
    console.log(raw);

    // 1. 객체/Map 형태 (e.g., data.currentWeights) 처리
    // 현재: { "MSFT" : 0.135 } 형태 또는 { "MSFT" : { "N" : "0.135" } } 형태
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      const normalized = Object.entries(raw).map(([ticker, weightValue]) => {
        let weight = 0;
        let rawWeightValue = weightValue;

        if (typeof weightValue === 'object' && weightValue !== null && weightValue.N) {
             // Case A: DynamoDB Number 타입 래퍼 ({ "N": "..." })
             weight = Number(weightValue.N) || 0;
             rawWeightValue = weightValue.N;
        } else if (typeof weightValue === 'number') {
             // Case B: 이미 언마샬링된 일반 숫자 (현재 데이터 구조!)
             weight = weightValue;
             rawWeightValue = weightValue;
        }

        // 🔍 **디버그**: 각 항목별 파싱 결과 확인
        console.log(`[Item - Map] Ticker: ${ticker}, RawWeight: ${rawWeightValue}, ParsedWeight: ${weight}`);

        return {
          ticker: ticker,
          weight: weight,
        };
      }).filter(h => h.ticker);
      
      console.log('--- normalizeHoldings: currentWeights 변환 결과 ---');
      console.log(normalized);
      return normalized;
    }

    // 2. 배열 형태 (data.initialHoldings) 처리 (이전 수정 버전과 동일하게 유지)
    if (Array.isArray(raw)) {
        const normalized = raw.map(h => {
            const item = h.M; 
            
            let ticker = '';
            let weight = 0;
            let rawWeightValue = 'N/A'; 

            if (item) {
                // DynamoDB List of Map (M) 구조
                const rawTicker = item.ticker && item.ticker.S;
                const rawWeight = item.weight && item.weight.N;
                rawWeightValue = rawWeight;

                if (rawTicker) {
                    ticker = rawTicker;
                    weight = Number(rawWeight) || 0;
                }
            } else if (h.ticker && h.weight !== undefined) {
                 // 일반 JSON 배열 구조
                 ticker = h.ticker;
                 weight = Number(h.weight) || 0;
                 rawWeightValue = h.weight;
            }

            // 🔍 **디버그**: 각 항목별 파싱 결과 확인
            console.log(`[Item - Array] Ticker: ${ticker}, RawWeight: ${rawWeightValue}, ParsedWeight: ${weight}`);

            return { ticker, weight };

        }).filter(h => h.ticker);
        
        console.log('--- normalizeHoldings: initialHoldings 변환 결과 ---');
        console.log(normalized);
        return normalized;
    }

    return [];
  };


  // 💡 [수정] 인증 및 데이터 fetching 로직 분리 및 추가 (이름 초기화 로직 추가)
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      auth.signinRedirect();
      return;
    }

    const fetchPortfolio = async () => {
      if (!auth.isAuthenticated || !portfolioId) {
        setIsLoading(false);
        return;
      }

      try {
        const token = auth.user.access_token;
        const res = await fetch(
          `${API_URL}/portfolios/${portfolioId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (!res.ok) {
          throw new Error('Failed to fetch portfolio data');
        }
        const data = await res.json();
        setPortfolio(data);
        
        // 💡 [추가] 포트폴리오 이름 초기 설정
        setPortfolioName(data.name || '');

        // 🔍 **디버그**: API에서 받은 전체 응답 데이터 확인
        // console.log('--- API 응답 데이터 (data) ---');
        // console.log(data);


        // 💡 [핵심]: currentWeights를 먼저 시도하고, 없으면 initialHoldings를 사용합니다.
        const rawHoldings = data.currentWeights || data.initialHoldings; 
        
        const normalizedHoldings = normalizeHoldings(rawHoldings); // 💡 변환 함수 사용
        
        if (normalizedHoldings.length > 0) {
          setSnapshots([
            {
              rebalanceAt: today,
              holdings: normalizedHoldings
            }
          ]);
        }
      } catch (e) {
        console.error('Portfolio fetch error:', e);
        // showCustomAlert('rebalanceModal.alert.loadError'); 
      } finally {
        setIsLoading(false);
      }
    };

    if (auth.isAuthenticated) {
      fetchPortfolio();
    }

  }, [auth, portfolioId, API_URL]);


  const showCustomAlert = (messageKey) => {
    setMessageModalContent(t(messageKey));
    setShowMessageModal(true);
  };

  const closeCustomAlert = () => {
    setShowMessageModal(false);
    setMessageModalContent('');
  };

  const addHolding = (snapIdx) => {
    setSnapshots(s => {
      const arr = [...s];
      arr[snapIdx].holdings.push({ ticker: '', weight: 0 });
      return arr;
    });
  };

  const updateHolding = (snapIdx, holdIdx, key, val) => {
    setSnapshots(s => {
      const snap = { ...s[snapIdx] };
      const holdings = [...snap.holdings];
      holdings[holdIdx] = {
        ...holdings[holdIdx],
        [key]: key === 'ticker' ? val : parseFloat(val) || 0
      };
      snap.holdings = holdings;
      const arr = [...s];
      arr[snapIdx] = snap;
      return arr;
    });
  };

  const removeHolding = (snapIdx, holdIdx) => {
    setSnapshots(s => {
      const arr = [...s];
      arr[snapIdx].holdings = arr[snapIdx].holdings.filter((_, i) => i !== holdIdx);
      return arr;
    });
  };

  const totalWeight = snapshots[0].holdings.reduce((sum, h) => sum + h.weight, 0);
  const isValid = Math.abs(totalWeight - 1) < 1e-6;

  // 💡 [수정] handleSubmit: 이름과 스냅샷을 포함하는 단일 페이로드 전송
  const handleSubmit = async () => {
    
    if (isSubmitting || isLoading) return; 
    setIsSubmitting(true); 

    if (!isValid) {
      showCustomAlert('rebalanceModal.alert.totalWeight100');
      setIsSubmitting(false); 
      return;
    }

    if (!auth.isAuthenticated) {
      showCustomAlert('rebalanceModal.alert.loginRequired');
      auth.signinRedirect();
      return;
    }
    
    try {
      const token = auth.user.access_token;
      
      // 💡 [수정] 이름과 스냅샷을 포함하는 단일 페이로드 구성
      const payload = {
        name: portfolioName, // 포트폴리오 이름 상태값 포함
        snapshots: snapshots // 기존 스냅샷 배열 포함
      };

      const res = await fetch(
        `${API_URL}/portfolios/${portfolioId}/rebalance`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload) // 💡 [수정] 새로운 페이로드 전송
        }
      );
      
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403) {
          showCustomAlert('rebalanceModal.alert.permissionError');
        } else if (res.status === 404) {
          showCustomAlert('rebalanceModal.alert.notFoundError');
        } else {
          // 백엔드에서 받은 에러 메시지가 있으면 사용
          const errorMessage = data.error || 'rebalanceModal.alert.generalError';
          showCustomAlert(errorMessage);
        }
        return;
      }
      onRebalanced();
      onClose();
    } catch (e) {
      console.error(e);
      showCustomAlert('rebalanceModal.alert.generalError');
    } finally {
        setIsSubmitting(false); 
    }
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={`${styles.modalContent} ${theme === 'dark' ? styles.dark : ''}`}>
        <button className={styles.closeButton} onClick={onClose}>×</button>
        <h2 className={styles.title}>{t('rebalanceModal.title')}</h2>

        {isLoading ? ( // 💡 [추가] 로딩 중 UI 표시
          <div style={{ padding: '20px', textAlign: 'center' }}>
            {t('rebalanceModal.loading') || '데이터를 불러오는 중입니다...'} 
          </div>
        ) : !portfolio ? ( // 💡 [추가] 데이터 로드 실패/없음
          <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
            {t('rebalanceModal.alert.loadError') || '포트폴리오 데이터를 불러오지 못했습니다.'}
          </div>
        ) : (
          <>
            {/* 💡 [추가] 포트폴리오 이름 입력 필드 */}
            <div className={styles.nameInputContainer} style={{marginBottom: '20px'}}>
              <input
                id="portfolioName"
                type="text"
                className={styles.inputName}
                value={portfolioName}
                onChange={(e) => setPortfolioName(e.target.value)}
                maxLength={20}
                // placeholder={t('newPortfolioModal.namePlaceholder')}
                disabled={isActionDisabled || isSubmitting}
                title={isActionDisabled ? t('rebalanceModal.loginRequiredTooltip') : ''}
              />
            </div>

            {snapshots.map((snap, si) => (
            <div key={si} className={styles.snapshotBlock}>
              
              {/* 헤더 추가 */}
              <div className={styles.holdingHeader}>
                <div>{t("backtest.TickerAndWeight")}</div>
                {/* <div>{t('newPortfolioModal.tableHeaders.ticker')}</div>
                <div>{t('newPortfolioModal.tableHeaders.weight')}</div>
                <div></div> */}
              </div>

              <div className={styles.holdingListContainer}>
                {snap.holdings.map((h, i) => (
                  <div key={i} className={styles.holdingRow}>
                    <TickerRow
                      disableDefaultStyles
                      className={styles.inputTicker}
                      value={h.ticker}
                      onChange={val => updateHolding(si, i, 'ticker', val)}
                      existingTickers={snap.holdings.map(x => x.ticker).filter(Boolean)}
                      disabled={isActionDisabled || isSubmitting} 
                      title={isActionDisabled ? t('rebalanceModal.loginRequiredTooltip') : ''}
                    />
                    <NumericFormat
                      className={styles.inputValue}
                      suffix="%"
                      decimalScale={2} 
                      fixedDecimalScale
                      allowNegative={false}
                      value={h.weight * 100}
                      onValueChange={values => updateHolding(si, i, 'weight', values.value / 100)}
                      inputMode="numeric"
                      disabled={isActionDisabled || isSubmitting} 
                      title={isActionDisabled ? t('rebalanceModal.loginRequiredTooltip') : ''}
                    />
                    <button
                      type="button"
                      className={styles.btnDelete}
                      onClick={() => removeHolding(si, i)}
                      disabled={isActionDisabled || isSubmitting} 
                      title={isActionDisabled ? t('rebalanceModal.loginRequiredTooltip') : ''}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div className={styles.totalWeight}>
                {t('rebalanceModal.totalWeightLabel')} <strong>{(totalWeight * 100).toFixed(2)}%</strong>
              </div>
              <div className={styles.actions}>
                <button
                    type="button"
                    className={styles.btnAdd}
                    onClick={() => addHolding(si)}
                    disabled={isActionDisabled || isSubmitting} 
                    title={isActionDisabled ? t('rebalanceModal.loginRequiredTooltip') : ''}
                  >
                    {t('rebalanceModal.addHoldingButton')}
                </button>
                <button
                  type="button"
                  className={styles.btnCreate}
                  onClick={handleSubmit}
                  disabled={isActionDisabled || !isValid || isSubmitting || isLoading} 
                  title={isActionDisabled ? t('rebalanceModal.loginRequiredTooltip') : isSubmitting ? '리밸런싱 제출 중...' : ''}
                >
                  {isSubmitting ? '리밸런싱 중...' : t('rebalanceModal.executeButton')}
                </button>
              </div>
            </div>
          ))}
          </>
        )}
      </div>


      {showMessageModal && (
        <MessageModal
          message={messageModalContent}
          onClose={closeCustomAlert}
        />
      )}
    </div>
  );
}